/**
 * Format API error responses into a user-readable string.
 *
 * FastAPI returns errors in two shapes:
 *   - HTTPException(detail="message")     → { detail: "message" }    (string)
 *   - Pydantic validation 422             → { detail: [{ loc, msg, type }, ...] }
 *
 * Naively writing `error.response?.data?.detail || "Generic message"` works
 * for the string form but the array form is truthy and gets rendered as
 * `[object Object]` (or, in <input setError /> handlers, swallowed entirely
 * by a `typeof === "string"` guard, leaving the user with a useless
 * "Something went wrong" message).
 *
 * Use `formatApiError(error, fallback)` everywhere.
 *
 * Examples:
 *   - Pydantic returns: [{loc:["body","email"], msg:"value is not a valid
 *     email address", type:"value_error"}]
 *     → "Email: value is not a valid email address"
 *   - HTTPException("Email already registered") → "Email already registered"
 *   - Network error → fallback string
 */

function _humaniseLoc(loc) {
  if (!Array.isArray(loc) || loc.length === 0) return "";
  // Drop the "body"/"query"/"path" prefix if present.
  const trimmed = loc[0] === "body" || loc[0] === "query" || loc[0] === "path"
    ? loc.slice(1)
    : loc;
  if (trimmed.length === 0) return "";
  const field = String(trimmed[trimmed.length - 1]);
  // Snake_case → Title Case ("accept_terms" → "Accept Terms")
  return field
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (!e || typeof e !== "object") return String(e);
        const msg = typeof e.msg === "string" ? e.msg : "";
        const fieldLabel = _humaniseLoc(e.loc);
        if (fieldLabel && msg) return `${fieldLabel}: ${msg}`;
        return msg || JSON.stringify(e);
      })
      .filter(Boolean)
      .join(" · ");
  }

  if (typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    return JSON.stringify(detail);
  }

  return String(detail);
}

/**
 * Top-level helper for axios errors. Tries detail → message → fallback.
 */
export function formatApiError(error, fallback = "Something went wrong. Please try again.") {
  if (!error) return fallback;
  const detail = error?.response?.data?.detail;
  const formatted = formatApiErrorDetail(detail);
  if (formatted) return formatted;
  if (error.message && typeof error.message === "string") return error.message;
  return fallback;
}

export default formatApiError;
